// Pure helpers behind the Reimbursement forms (PrescriptionForm, ClaimForm, ReuploadForm): upload
// state, per-field validation and the payloads they send. Kept out of the components so mocha can
// pin them (formHelpers.spec.js).
import { UPLOAD_FIELD, FILE_NAME_FIELD } from './reimbursementStatus';

// --- Upload state ----------------------------------------------------------------------------------
// { [slot]: { attempt, file, status: 'uploading' | 'done' | 'error' | 'invalid', progress, url, error } }
// Files upload as soon as they are picked (ADR-0003). Each pick gets a new attempt number, and an
// answer for any other attempt is ignored, so a slow upload the member already replaced, cancelled
// or removed can never overwrite the newer state.

const isCurrent = (state, action) => state[action.slot] && state[action.slot].attempt === action.attempt;

export const uploadsReducer = (state, action) => {
    const prev = state[action.slot];
    switch (action.type) {
        case 'start':
            return { ...state, [action.slot]: { attempt: action.attempt, file: action.file, status: 'uploading', progress: 0, url: null, error: null } };
        case 'progress':
            if (!isCurrent(state, action) || prev.status !== 'uploading') return state;
            return { ...state, [action.slot]: { ...prev, progress: Math.min(99, Math.max(0, Math.round(action.progress))) } };
        case 'done':
            if (!isCurrent(state, action)) return state;
            return { ...state, [action.slot]: { ...prev, status: 'done', progress: 100, url: action.url, error: null } };
        case 'fail':
            // The file stays, so the member can retry it.
            if (!isCurrent(state, action)) return state;
            return { ...state, [action.slot]: { ...prev, status: 'error', progress: 0, url: null, error: action.error } };
        case 'invalid':
            // A file that cannot be used never replaces one already uploaded (or uploading).
            if (prev) return { ...state, [action.slot]: { ...prev, error: action.error } };
            return { ...state, [action.slot]: { attempt: 0, file: null, status: 'invalid', progress: 0, url: null, error: action.error } };
        case 'remove': {
            if (!prev) return state;
            const next = { ...state };
            delete next[action.slot];
            return next;
        }
        default:
            return state;
    }
};

export const uploadsBusy = (uploads = {}) => Object.values(uploads).some((upload) => upload?.status === 'uploading');

// Why an upload slot is not ready yet, or null when it is (or is optional and empty).
const uploadError = (upload, missing) => {
    if (upload?.status === 'uploading') return 'Please wait for this upload to finish.';
    if (upload?.url) return null;
    if (upload?.status === 'error') return 'This upload failed. Retry it, or choose another file.';
    return missing;
};

// --- Field ids and error focus ------------------------------------------------------------------

export const fieldDomId = (formId, name) => `${formId}-${name}`;

// The first field with an error, in the order the form shows them.
export const firstErrorField = (errors = {}, order = []) => (
    order.find((name) => errors[name]) || Object.keys(errors).find((name) => errors[name]) || null
);

const compact = (errors) => Object.fromEntries(Object.entries(errors).filter(([, message]) => message));

// Steps of a wizard for the Stepper: [{ label, state }], `step` counting from 1.
export const wizardSteps = (labels, step) => labels.map((label, index) => ({
    label,
    state: index + 1 < step ? 'done' : index + 1 === step ? 'current' : 'upcoming',
}));

// --- Reimbursement Prescription ---------------------------------------------------------------

export const PRESCRIPTION_FIELD_ORDER = [
    'patient', 'clinic_name', 'clinic_address', 'clinic_doctor', 'clinic_contact',
    'prescription_file', 'clinical_notes', 'upper_occlusal_photo', 'lower_occlusal_photo', 'problem_area_photo',
    'additional_photo', 'pre_op_xray_1', 'pre_op_xray_2',
];

const PRESCRIPTION_REQUIRED_UPLOADS = {
    prescription_file: 'Please upload the prescription.',
    upper_occlusal_photo: 'Please upload the Upper Occlusal photo.',
    lower_occlusal_photo: 'Please upload the Lower Occlusal photo.',
    problem_area_photo: 'Please upload the Problem Area photo.',
};
const PRESCRIPTION_OPTIONAL_UPLOADS = ['additional_photo', 'pre_op_xray_1', 'pre_op_xray_2'];

const blank = (value) => !String(value ?? '').trim();

// { field: message } for everything that stops the prescription going to review.
export const validatePrescription = ({ members = [], patientUid, fields = {}, uploads = {} }) => {
    const errors = {
        patient: !members.length
            ? 'We could not load the people on your policy. Please log out and log in again.'
            : !members.some((member) => member.uid === patientUid) ? 'Please select the patient.' : null,
        clinic_name: blank(fields.clinic_name) ? 'Please enter the clinic name.' : null,
        clinic_address: blank(fields.clinic_address) ? 'Please enter the clinic address.' : null,
        clinic_doctor: blank(fields.clinic_doctor) ? "Please enter the doctor's name." : null,
        clinic_contact: blank(fields.clinic_contact) ? 'Please enter the clinic contact.' : null,
        clinical_notes: blank(fields.clinical_notes) ? 'Please add the clinical notes.' : null,
    };
    Object.entries(PRESCRIPTION_REQUIRED_UPLOADS).forEach(([slot, missing]) => { errors[slot] = uploadError(uploads[slot], missing); });
    // Optional photos may be left out, but not left half-uploaded or failed.
    PRESCRIPTION_OPTIONAL_UPLOADS.forEach((slot) => { errors[slot] = uploads[slot] ? uploadError(uploads[slot], null) : null; });
    return compact(errors);
};

export const prescriptionPayload = ({ fields, patientUid, uploads }) => {
    const payload = { ...fields, uid: patientUid };
    payload.reimbursement_approval_file_url = uploads.prescription_file?.url;
    payload.upper_occlusal_photo_url = uploads.upper_occlusal_photo?.url;
    payload.lower_occlusal_photo_url = uploads.lower_occlusal_photo?.url;
    payload.problem_area_photo_url = uploads.problem_area_photo?.url;
    if (uploads.additional_photo?.url) payload.additional_photo_url = uploads.additional_photo.url;
    const preOpXrayUrls = [uploads.pre_op_xray_1?.url, uploads.pre_op_xray_2?.url].filter(Boolean);
    if (preOpXrayUrls.length) payload.pre_op_xray_urls = preOpXrayUrls;
    return payload;
};

// --- Proof of treatment (ClaimForm) ------------------------------------------------------------
// A proof covers the approved procedures the member chose (the treatments they have had), with the
// invoice and the same three photos as the prescription, taken after the treatment, plus optional
// X-rays and supporting documents. A Reimbursement approved before treatment plans has nothing to
// choose, so its form starts at Documents.

export const CLAIM_STEP_LABELS = ['Procedures', 'Documents', 'Review'];

// The four documents every proof needs.
export const PROOF_UPLOADS = [
    'invoice_receipt',
    'post_treatment_upper_occlusal_photo',
    'post_treatment_lower_occlusal_photo',
    'post_treatment_problem_area_photo',
];

// Optional, up to five of each, sent in slot order.
export const PROOF_XRAY_SLOTS = UPLOAD_FIELD.post_treatment_xray_urls;
export const SUPPORTING_DOCUMENT_SLOTS = UPLOAD_FIELD.supporting_document_urls;

const PROOF_UPLOAD_MISSING = {
    invoice_receipt: 'Please upload the invoice or receipt.',
    post_treatment_upper_occlusal_photo: 'Please upload the Upper Occlusal photo.',
    post_treatment_lower_occlusal_photo: 'Please upload the Lower Occlusal photo.',
    post_treatment_problem_area_photo: 'Please upload the Problem Area photo.',
};

export const CLAIM_FIELD_ORDER = ['procedures', ...PROOF_UPLOADS, ...PROOF_XRAY_SLOTS, ...SUPPORTING_DOCUMENT_SLOTS, 'declaration'];

// { field: message } for one step of the proof form, named by its label ('Procedures', 'Documents',
// 'Review'). `planned` is false for a Reimbursement with no treatment plan (nothing to choose).
export const validateClaimStep = (step, { selected = [], planned = true, uploads = {}, declarationAccepted = false } = {}) => {
    if (step === 'Procedures') {
        return planned && !selected.length ? { procedures: "Choose the treatments you've had." } : {};
    }
    if (step === 'Documents') {
        const required = PROOF_UPLOADS.map((slot) => [slot, uploadError(uploads[slot], PROOF_UPLOAD_MISSING[slot])]);
        // Optional files may be left out, but not left half-uploaded or failed.
        const optional = [...PROOF_XRAY_SLOTS, ...SUPPORTING_DOCUMENT_SLOTS].map((slot) => [slot, uploads[slot] ? uploadError(uploads[slot], null) : null]);
        return compact(Object.fromEntries([...required, ...optional]));
    }
    if (step === 'Review') {
        return compact({ declaration: declarationAccepted ? null : 'Please accept the declaration to submit.' });
    }
    return {};
};

// How many slots of an optional group to show: at least one, every one in use, and as many as the
// member asked for with "Add another".
export const slotsToShow = (slots, uploads = {}, requested = 1) => {
    const lastUsed = slots.reduce((last, slot, index) => (uploads[slot] ? index + 1 : last), 0);
    return Math.min(slots.length, Math.max(1, requested, lastUsed));
};

// After the Reimbursement reloads, the ticks still waiting for proof are kept and the rest dropped.
export const keepChoices = (selected = [], choiceIds = []) => {
    const valid = choiceIds.map(String);
    return {
        kept: selected.filter((id) => valid.includes(String(id))),
        dropped: selected.filter((id) => !valid.includes(String(id))),
    };
};

// The model field an upload slot fills ('invoice_receipt' → 'invoice_receipt_url').
const urlFieldOf = (slot) => Object.keys(UPLOAD_FIELD).find((field) => UPLOAD_FIELD[field] === slot);

// The proof as POST /claim takes it: the chosen procedures (`procedureIds`; null leaves them out
// for a Reimbursement with no plan), the four required files with the names the member picked, the
// optional X-rays and supporting documents in slot order, and the declaration. Never sends clinic,
// treatment or payout details, nor the old single post_treatment_photo_url.
export const claimPayload = ({ reimbursementId, uploads = {}, procedureIds = null }) => {
    const payload = { prescription_id: reimbursementId || '' };
    if (Array.isArray(procedureIds)) payload.procedure_ids = procedureIds;
    PROOF_UPLOADS.forEach((slot) => {
        const field = urlFieldOf(slot);
        payload[field] = uploads[slot]?.url;
        payload[FILE_NAME_FIELD[field]] = uploads[slot]?.file?.name;
    });
    payload.post_treatment_xray_urls = PROOF_XRAY_SLOTS.map((slot) => uploads[slot]?.url).filter(Boolean);
    payload.supporting_document_urls = SUPPORTING_DOCUMENT_SLOTS.map((slot) => uploads[slot]?.url).filter(Boolean);
    payload.declaration_accepted = 'true';
    return payload;
};

// --- Re-upload -------------------------------------------------------------------------------

// One upload slot per requested file (x-rays are two slots, at least one required).
export const reuploadSlots = (requestFields = []) => requestFields.flatMap((field) => {
    const upload = UPLOAD_FIELD[field];
    if (!upload) return [];
    return Array.isArray(upload)
        ? upload.map((slot, index) => ({ field, slot, index, required: index === 0 }))
        : [{ field, slot: upload, index: 0, required: true }];
});

export const validateReupload = (slots, uploads = {}) => compact(Object.fromEntries(slots.map(({ slot, required }) => [
    slot,
    required || uploads[slot] ? uploadError(uploads[slot], required ? 'Please upload a new file.' : null) : null,
])));

// { <field>: new url(s) }, plus the picked names for proof-of-treatment files.
export const reuploadPayload = ({ requestFields = [], uploads = {}, isTreatment = false }) => {
    const payload = {};
    requestFields.forEach((field) => {
        const upload = UPLOAD_FIELD[field];
        if (!upload) return;
        payload[field] = Array.isArray(upload)
            ? upload.map((slot) => uploads[slot]?.url).filter(Boolean)
            : uploads[upload]?.url;
        if (isTreatment && FILE_NAME_FIELD[field] && !Array.isArray(upload)) payload[FILE_NAME_FIELD[field]] = uploads[upload]?.file?.name;
    });
    return payload;
};
